<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack href="/license/reminder" />
        </div>
      </div>
    </div>
    <div class="mc-form mt-3">
      <div class="clearfix mb-3">
        <div class="float-start">
          <h4 class="card-title">{{ $t('license.text.reminderInfo') }}</h4>
        </div>
        <div class="float-end d-flex">
          <McButton
            icon="icon-fill_close"
            variant="secondary"
            :text="$t('global.cancel')"
            class="mx-2"
            :disabled="!isChanged"
            @click="toCancel"
          />
          <McButton
            v-if="hasAccess(['license_notification:create', 'license_notification:update'])"
            icon="icon-outline_save"
            variant="primary"
            :text="$t('global.save')"
            :disabled="isSubmitting || !isChanged"
            @click="saveReminder"
          />
        </div>
      </div>
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <b-card>
            <b-skeleton width="85%" />
            <b-skeleton width="55%" />
            <b-skeleton width="70%" />
          </b-card>
        </template>
        <b-card>
          <b-row>
            <div class="clearfix">
              <h4 class="card-title">
                {{ $t('notification.setting.emailNotification.mailGroup') }}
              </h4>
            </div>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="4">
              <b-form-group
                :label="$t('notification.setting.emailNotification.groupName')"
                label-for="schedule-name"
                label-class="required"
              >
                <b-form-input
                  id="schedule-name"
                  v-model="form.name_group_email"
                  :placeholder="$t('notification.setting.emailNotification.groupName')"
                  :class="{ 'is-invalid': $v.form.name_group_email.$error }"
                />
                <div v-if="$v.form.name_group_email.$error" class="invalid-feedback">
                  <span v-if="!$v.form.name_group_email.required">
                    {{ $t('notification.setting.emailNotification.groupName') }}
                    {{ $t('global.validation.required') }}
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <Recipient :isAdding="isAdding" :ngroupId="$route.params.id" />
          </b-row>
        </b-card>
        <div class="clearfix mb-3">
          <h4 class="card-title">{{ $t('license.text.reminderSetting') }}</h4>
        </div>
        <b-card>
          <b-row>
            <b-col>
              <b-form-group>
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label>{{ $t('license.text.emailSent') }}</label>
                    <span v-b-tooltip.topleft :title="`${$t('license.text.reminderTooltip')}`">
                      <i class="icon-outline_info"></i>
                    </span>
                  </div>
                </template>
                <b-input-group :append="$t('license.text.expiredInterval')">
                  <b-form-input
                    v-model="form.reminder"
                    required
                    type="number"
                    min="0"
                    @keypress="isNumber($event)"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group :label="$t('license.text.repetitionLabel')" label-for="repetition">
                <b-row>
                  <b-col cols="4">
                    <b-form-radio v-model="form.repetition" value="O" class="mt-2">
                      <label class="radio-label">{{ $t('license.object.once') }}</label>
                    </b-form-radio>
                  </b-col>
                  <b-col>
                    <b-form-radio v-model="form.repetition" value="ED" class="mt-2">
                      <label class="radio-label">{{ $t('license.object.everyday') }}</label>
                    </b-form-radio>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-skeleton-wrapper>
      <div class="clearfix mb-3" v-if="!isAdding">
        <h4 class="card-title">{{ $t('license.text.docsType') }}</h4>
      </div>
      <b-skeleton-wrapper :loading="docsLoading" v-if="!isAdding">
        <template #loading>
          <b-card>
            <b-skeleton width="85%" />
            <b-skeleton width="55%" />
            <b-skeleton width="70%" />
          </b-card>
        </template>
        <b-card>
          <span v-if="docTrueCount == 1" class="text-warning text-sm-italic">{{
            $t('license.validation.atLeastChooseOne')
          }}</span>
          <div class="mc-notification">
            <div class="row">
              <div class="col-md-6 mt-4">
                <h5 class="card-title">{{ $t('license.object.vehicleDocument') }}</h5>
              </div>
              <div class="col-md-6 align-self-end">
                <div class="float-end mt-4">
                  <h6
                    class="text-primary"
                    v-if="!isCheckAllFleetdoc"
                    @click="checkAllVehicleDocs(true)"
                  >
                    {{ $t('global.action.activate') }}
                  </h6>
                  <h6 class="text-primary" v-else @click="checkAllVehicleDocs(false)">
                    {{ $t('global.action.nonactivate') }}
                  </h6>
                </div>
              </div>
            </div>
            <div class="border border-1 p-3 rounded mt-3 mc-notification-item">
              <div v-for="docs in vehicleDocs">
                <div class="row">
                  <div class="col-md-6 mt-3">
                    <h6>{{ docs.text }}</h6>
                  </div>
                  <div class="col-md-6 align-self-end">
                    <div class="float-end mt-3">
                      <div class="form-check form-switch form-switch-md mb-3">
                        <input
                          v-model="vehicleDocsChecker[docs.text]"
                          class="form-check-input"
                          type="checkbox"
                          @change="saveLicense(docs.text)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mt-4">
                <h5 class="card-title">{{ $t('license.object.driverDocument') }}</h5>
              </div>
              <div class="col-md-6 align-self-end">
                <div class="float-end mt-4">
                  <h6
                    class="text-primary"
                    v-if="!isCheckAllDriverdoc"
                    @click="checkAllDriverDocs(true)"
                  >
                    {{ $t('global.action.activate') }}
                  </h6>
                  <h6 class="text-primary" v-else @click="checkAllDriverDocs(false)">
                    {{ $t('global.action.nonactivate') }}
                  </h6>
                </div>
              </div>
            </div>
            <div class="border border-1 p-3 rounded mt-3 mc-notification-item">
              <div v-for="docs in userDocs">
                <div class="row">
                  <div class="col-md-6 mt-3">
                    <h6>{{ docs.text }}</h6>
                  </div>
                  <div class="col-md-6 align-self-end">
                    <div class="float-end mt-3">
                      <div class="form-check form-switch form-switch-md mb-3">
                        <input
                          v-model="userDocsChecker[docs.text]"
                          class="form-check-input"
                          type="checkbox"
                          @change="saveLicense(docs.text)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-skeleton-wrapper>
    </div>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import PageHeader from '@/components/page-header'
import { Helper } from '@/mixin/helper'
import Layout from '@/router/layouts/main'
import { GET_USER_DOC_TYPES } from '@/services/api/license/userDocs'
import Recipient from './recipient'
import {
  CREATE_EMAIL_NOTIFICATION,
  GET_EMAIL_NOTIFICATION,
  GET_LICENSE_REMINDER,
  GET_VEHICLE_DOC_TYPES,
  UPDATE_EMAIL_LICENSE_REMINDER,
  UPDATE_EMAIL_NOTIFICATION,
} from '@/services/api/license/vehicleDocs'
import { required } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Pengingat Lisensi',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    Recipient,
  },
  mixins: [Helper],
  apollo: {
    vehicleDocType: {
      query: GET_VEHICLE_DOC_TYPES,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        this.vehicleDocs = data.vehicleDocType.vehicleDocTypes.data
        this.vehicleDocsChecker = this.vehicleDocs.reduce(
          (acc, item) => {
            if (item.text) {
              acc[item.text] = false
            }
            return acc
          },
          { all: false }
        )
        this.currentVehicleDocsChecker = this.vehicleDocs.reduce(
          (acc, item) => {
            if (item.text) {
              acc[item.text] = false
            }
            return acc
          },
          { all: false }
        )
      },
    },
    userDocTypes: {
      query: GET_USER_DOC_TYPES,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        }
      },
      result({ data, loading }) {
        if (loading) return
        this.userDocs = data.userDocTypes.userDocTypes.data
        this.userDocsChecker = this.userDocs.reduce(
          (acc, item) => {
            if (item.text) {
              acc[item.text] = false
            }
            return acc
          },
          { all: false }
        )
        this.currentUserDocsChecker = this.userDocs.reduce(
          (acc, item) => {
            if (item.text) {
              acc[item.text] = false
            }
            return acc
          },
          { all: false }
        )
      },
    },
  },
  data() {
    return {
      form: {
        name_group_email: '',
        reminder: 3,
        repetition: 'O',
      },
      currentForm: {
        name_group_email: '',
        reminder: 3,
        repetition: 'O',
      },
      isLoading: false,
      docsLoading: false,
      userDocs: [],
      vehicleDocs: [],
      isSubmitting: false,
      userDocsChecker: {},
      vehicleDocsChecker: {},
      currentUserDocsChecker: {},
      currentVehicleDocsChecker: {},
    }
  },
  validations: {
    form: {
      name_group_email: { required },
    },
  },
  computed: {
    docTrueCount() {
      let countTrue = 0
      Object.keys(this.userDocsChecker).forEach((item) => {
        if (this.userDocsChecker[item]) countTrue += 1
      })
      Object.keys(this.vehicleDocsChecker).forEach((item) => {
        if (this.vehicleDocsChecker[item]) countTrue += 1
      })
      return countTrue
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    title() {
      return this.$route.params.id
        ? this.$t('license.title.updateReminder')
        : this.$t('license.title.createReminder')
    },
    emailNotifId() {
      return parseInt(this.$route.params.id)
    },
    isAdding() {
      return typeof this.$route.params.id === 'undefined'
    },
    isCheckAllFleetdoc() {
      let self = this
      let isChecked = true
      Object.keys(this.vehicleDocsChecker).forEach(function (key) {
        if (key !== 'all' && !self.vehicleDocsChecker[key]) {
          isChecked = false
        }
      })

      return isChecked
    },
    isCheckAllDriverdoc() {
      let self = this
      let isChecked = true
      Object.keys(this.userDocsChecker).forEach(function (key) {
        if (key !== 'all' && !self.userDocsChecker[key]) {
          isChecked = false
        }
      })

      return isChecked
    },
    isChanged() {
      let result = false
      Object.keys(this.form).forEach((key) => {
        if (this.form[key] != this.currentForm[key]) {
          result = true
        }
      })
      return result
    },
  },
  async mounted() {
    if (!this.isAdding) {
      await this.$nextTick()
      await this.showData(this.emailNotifId)
      await this.getDocs(this.emailNotifId)
    }
  },
  methods: {
    async showData(id, needLoad = true) {
      if (needLoad) this.isLoading = true
      const res = await this.$apollo.query({
        query: GET_EMAIL_NOTIFICATION,
        variables: {
          filter: {
            emailNotificationId: id,
          },
        },
        fetchPolicy: 'no-cache',
      })

      const item = res.data.emailNotification.emailNotification
      this.form = {
        name_group_email: item.nameGroupEmail,
        reminder: item.reminder,
        repetition: item.repetition ? 'ED' : 'O',
      }

      this.currentForm = {
        name_group_email: item.nameGroupEmail,
        reminder: item.reminder,
        repetition: item.repetition ? 'ED' : 'O',
      }

      setTimeout(() => {
        this.isSubmitting = false
        if (needLoad) this.isLoading = false
      }, 1000)
    },
    async getDocs(id = null, needLoad = true) {
      if (needLoad) this.docsLoading = true
      const res = await this.$apollo.query({
        query: GET_LICENSE_REMINDER,
        variables: {
          filter: {
            emailNotificationId: id,
          },
        },
        fetchPolicy: 'no-cache',
      })
      const docs = res.data.emailLicenseNotificationDetails.emailLicenseNotificationDetails
      docs.docTypes.forEach((item) => {
        if (this.vehicleDocsChecker.hasOwnProperty(item)) {
          this.vehicleDocsChecker[item] = true
        }
        if (this.userDocsChecker.hasOwnProperty(item)) {
          this.userDocsChecker[item] = true
        }
        if (this.currentVehicleDocsChecker.hasOwnProperty(item)) {
          this.currentVehicleDocsChecker[item] = true
        }
        if (this.currentUserDocsChecker.hasOwnProperty(item)) {
          this.currentUserDocsChecker[item] = true
        }
      })
      setTimeout(() => {
        this.isSubmitting = false
        if (needLoad) this.docsLoading = false
      }, 1000)
    },
    validate() {
      // Checking validation
      this.$v.$touch()
      if (this.$v.form.$error) {
        return false
      }
      return true
    },
    toCancel() {
      if (this.isAdding) {
        this.form = {
          name_group_email: '',
          reminder: 3,
          repetition: 'O',
        }
      } else {
        this.form = { ...this.currentForm }
      }
    },
    saveReminder() {
      let validate = this.validate()
      if (!validate) {
        return
      }
      this.isSubmitting = true
      const input = {
        nameGroupEmail: this.form.name_group_email,
        reminder: parseInt(this.form.reminder),
        repetition: this.form.repetition === 'ED',
        type: 'license',
      }

      if (this.isAdding) {
        this.createReminder(input)
      } else {
        this.updateReminder(input, this.emailNotifId)
      }
    },
    async createReminder(data) {
      const create = await this.$apollo.mutate({
        mutation: CREATE_EMAIL_NOTIFICATION,
        variables: {
          input: data,
        },
      })
      const id = create.data.createEmailNotification.emailNotification.emailNotificationId
      this.isSubmitting = false
      if (!create.data.createEmailNotification.isSuccessful) {
        this.$mcToast.danger(
          `${this.$t('global.alert.text.failedSaved')}, ${
            create.data.createEmailNotification.message
          }`
        )
        return
      }
      this.$mcToast.success(this.$t('global.alert.text.saved'))

      this.$router.push({
        name: 'Update License Reminder',
        params: { id },
      })
    },
    async updateReminder(data, id) {
      const update = await this.$apollo.mutate({
        mutation: UPDATE_EMAIL_NOTIFICATION,
        variables: {
          input: {
            ...data,
            emailNotificationId: id,
          },
        },
      })
      if (!update.data.updateEmailNotification.isSuccessful) {
        this.$mcToast.danger(
          `${this.$t('global.alert.text.noChanges')}, ${
            update.data.updateEmailNotification.message
          }`
        )
        this.isSubmitting = false
        return
      }
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      this.showData(id)
      this.getDocs(id, false)
    },
    checkAllVehicleDocs(event) {
      this.vehicleDocsChecker.all = event
      this.isSubmitting = true
      let obj = this.vehicleDocsChecker
      Object.keys(obj).forEach(function (key) {
        if (key !== 'all') {
          obj[key] = event
        }
      })
      this.vehicleDocsChecker = obj
      const docs = this.vehicleDocs.map((item) => item.text)
      this.saveAllLicense(docs, event)
    },
    checkAllDriverDocs(event) {
      this.userDocsChecker.all = event
      this.isSubmitting = true
      let obj = this.userDocsChecker
      Object.keys(obj).forEach(function (key) {
        if (key !== 'all') {
          obj[key] = event
        }
      })
      this.userDocsChecker = obj
      const docs = this.userDocs.map((item) => item.text)
      this.saveAllLicense(docs, event)
    },
    checkAllUserDocs(event) {
      this.userDocsChecker.all = event
      this.isSubmitting = true
      let obj = this.userDocsChecker
      Object.keys(obj).forEach(function (key) {
        if (key !== 'all') {
          obj[key] = event
        }
      })
      this.userDocsChecker = obj
      const docs = this.userDocs.map((item) => item.text)

      this.saveAllLicense(docs, event)
    },
    async saveAllLicense(docs, event) {
      if (docs.length > 0) {
        const update = await this.$apollo.mutate({
          mutation: UPDATE_EMAIL_LICENSE_REMINDER,
          variables: {
            input: {
              docTypes: docs,
              status: event,
              emailNotificationId: this.emailNotifId,
            },
          },
        })
        if (!update.data.updateEmailLicenseNotificationDetails.isSuccessful) {
          this.$mcToast.error(
            `${this.$t('global.alert.text.noChanges')}, ${
              update.data.updateEmailLicenseNotificationDetails.message
            }`
          )
          return
        }
        this.$mcToast.success(this.$t('global.alert.text.changed'))
      }
    },
    async saveLicense(docs) {
      this.docsLoading = true
      let state
      let docsType
      if (this.vehicleDocsChecker.hasOwnProperty(docs)) {
        state = this.vehicleDocsChecker[docs]
        docsType = 'vehicle'
      } else if (this.userDocsChecker.hasOwnProperty(docs)) {
        state = this.userDocsChecker[docs]
        docsType = 'driver'
      }

      if (!state && this.docTrueCount == 0) {
        this.$mcToast.warning(this.$t('license.validation.atLeastChooseOne'))
        const isVehicleDocsType = docsType === 'vehicle'
        const targetChecker = isVehicleDocsType ? this.vehicleDocsChecker : this.userDocsChecker
        targetChecker[docs] = true
        return setTimeout(() => {
          this.docsLoading = false
        }, 200)
      }
      const input = {
        docTypes: [docs],
        status: state,
        emailNotificationId: this.emailNotifId,
      }
      const update = await this.$apollo.mutate({
        mutation: UPDATE_EMAIL_LICENSE_REMINDER,
        variables: {
          input: input,
        },
      })
      if (!update.data.updateEmailLicenseNotificationDetails.isSuccessful) {
        this.$mcToast.error(
          `${this.$t('global.alert.text.noChanges')}, ${
            update.data.updateEmailLicenseNotificationDetails.message
          }`
        )
        return setTimeout(() => {
          this.docsLoading = false
        }, 200)
      }
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      this.docsLoading = false
    },
  },
}
</script>
